import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { useQueryCache } from "react-query";
import { message, Pagination } from "antd";

import * as ajaxEndpoints from "../../../../api/ajax-endpoints";
import ButtonLoader, { Loader } from "../../../../UI/Loaders/Loaders";
import useEachInvestorsInvestment, {
  EACH_INVESTORS_INVESTMENT_KEY,
} from "../../../../custom-hooks/useEachInvestorsInvestment";
import useEachInvestorInvestmentSummary from "../../../../custom-hooks/useEachInvestorInvestmentSummary";
import useAnInvestmentProduct from "../../../../custom-hooks/useAnInvestmentProduct";
import { formatTenor } from "../../../../helpers/formatTenor";
import { DepositType, toDepositTypeId } from "../../../../helpers/depositType";
import { IFunderInvestmentListItem } from "../../../../interfaces/funderInvestment";
import { formatMoney } from "../../../../helpers/formatter";
import { attachClassNameToTag } from "../../../../helpers/attachClassNameToTag";
import {
  handleFundInvestmentModalToggle,
  handleInvestmentWithdrawalModalToggle,
  handleRollOverModalToggle,
  InvestmentStatus,
} from "./helper";
import { getCurrencyInfo } from "../../../FunderInvestmentProduct/helper";
import { ROLE_TYPES } from "../../../../helpers/roleTypes";
import useInvestmentClosure from "../../../../custom-hooks/useInvestmentClosure";
import CloseInvestmentModal from "../../../../NewComponents/CloseInvestmentModal";
import { ISelectedRecord } from "./EachInvestorInvestment";

import styles from "../InvestorsInvestment.module.scss";

const PAGE_SIZE = 20;

interface IInvestorInvestmentProps {
  id: string | number;
  data: any;
  investment_product_id: string | number;
  setSelectedRecord: React.Dispatch<
    React.SetStateAction<ISelectedRecord | undefined>
  >;
  selectedFund: string;
}

interface IRootState {
  authReducer: {
    loggedInUser: {
      roles: string[];
      first_name: string;
      last_name: string;
    } | null;
  };
}

const InvestorInvestment: React.FC<IInvestorInvestmentProps> = ({
  id,
  data,
  investment_product_id,
  setSelectedRecord,
  selectedFund: currency,
}) => {
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] =
    useState<boolean>(false);
  const [investmentID, setInvestmentID] = useState<number | null>(null);
  const [maturityDate, setMaturityDate] = useState<string>("");
  const [page, setPage] = useState(1);

  // A different currency is a different list; start it from the top.
  useEffect(() => setPage(1), [currency]);

  const loggedInUser = useSelector(
    (state: IRootState) => state?.authReducer?.loggedInUser,
  );
  const roles = loggedInUser?.roles;
  const history = useHistory();
  const queryCache = useQueryCache();

  const {
    data: investmentsData,
    status: investmentsStatus,
    error,
  } = useEachInvestorsInvestment({
    id: Number(id),
    currency,
    investmentProductId: Number(investment_product_id),
    page,
    pageSize: PAGE_SIZE,
  });

  // The rows no longer carry a deposit type, and every row here is the same
  // product, so it is read once from the product for this currency.
  const { data: product } = useAnInvestmentProduct(
    Number(investment_product_id),
  );
  const depositType = toDepositTypeId(
    product?.investment_product_details?.find(
      (detail: any) => detail?.currency_code === currency,
    )?.deposit_type,
  );
  const isFixedDeposit = depositType === DepositType.FixedDeposit;
  const currencySymbol = getCurrencyInfo(currency)?.symbol;
  const investments: IFunderInvestmentListItem[] = investmentsData?.data ?? [];

  const { data: investmentSummaryData, status: investmentSummaryStatus } =
    useEachInvestorInvestmentSummary({
      id: Number(id),
      currency,
      investmentProductId: Number(investment_product_id),
    });

  const { mutate: closeInvestment, isLoading: isLoadingInvestmentClosure } =
    useInvestmentClosure(
      ajaxEndpoints.CLOSE_WITHDRAW_INVESTMENT,
      (responseData: { message?: string }) => {
        message.success(
          responseData.message || "Investment closed successfully!",
        );
        setIsConfirmationModalOpen(false);
        // Every page of this investor's list, not only the one on screen.
        queryCache.invalidateQueries(
          (query) => query.queryKey?.[1] === EACH_INVESTORS_INVESTMENT_KEY,
        );
      },
    );

  const handleInvestmentClosure = async (closeDate: string) => {
    await closeInvestment({
      investment_id: investmentID,
      close_date: closeDate,
    });
  };

  return (
    <div>
      {investmentsStatus === "loading" ? (
        <Loader centered={true} text="Loading ..." />
      ) : error ? (
        <div>
          <div className="grid__padding animated fadeInRight">
            <h4 className="center-txt py-5">
              <i className="fas fa-bell" /> Unable to Fetch Investor's
              Investment
            </h4>
          </div>
        </div>
      ) : investments.length > 0 ? (
        <div className="datatable-wrapper">
          <div className=" speed-1x animated fadeInRight">
            {/*Datatable || Loader || Error Message*/}
            <div className="row mt-4">
              <div className="col-md-4 d-flex flex-column">
                <div className={styles.rightBorder}>
                  <div className={styles.investmentTitle}>
                    Total Active Investments
                  </div>
                  <div className={styles.investmentSubTitle}>
                    {investmentSummaryStatus === "loading" ? (
                      <ButtonLoader />
                    ) : (
                      investmentSummaryData?.total_active_investment || "N/A"
                    )}
                  </div>
                </div>
              </div>
              <div className="col-md-4 d-flex flex-column">
                <div className={styles.rightBorder}>
                  <div className={styles.investmentTitle}>
                    Total Amount Invested
                  </div>
                  <div className={styles.investmentSubTitle}>
                    {investmentSummaryStatus === "loading" ? (
                      <ButtonLoader />
                    ) : (
                      formatMoney(
                        investmentSummaryData?.total_amount_invested,
                        currencySymbol,
                      ) || "N/A"
                    )}
                  </div>
                </div>
              </div>
              <div className="col-md-4 d-flex flex-column">
                <div className={styles.rightBorder}>
                  <div className={styles.investmentTitle}>
                    Total Interest Payable
                  </div>
                  <div className={styles.investmentSubTitle}>
                    {investmentSummaryStatus === "loading" ? (
                      <ButtonLoader />
                    ) : (
                      formatMoney(
                        investmentSummaryData?.total_interest_payable,
                        currencySymbol,
                      ) || "N/A"
                    )}
                  </div>
                </div>
              </div>
            </div>
            <h5 className={`${styles.portfolio} mb-3`}>Investments</h5>

            <div className="row m-x-sm m-y-2 grid__padding">
              <div className="table-responsive">
                <table className="table">
                  <thead className="bg-lighter-grey">
                    <tr>
                      <th>Reference No</th>
                      <th>Plan</th>
                      <th>Type</th>
                      <th>Tenor</th>
                      <th>Total Deposits</th>
                      <th>Total Withdrawals</th>
                      <th>Status</th>
                      <th>Start Date</th>
                      <th>Maturity Date</th>
                      <th>Manage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {investments.map((request: IFunderInvestmentListItem) => {
                      const {
                        id: investment_id,
                        reference_number,
                        investment_name,
                        product_name,
                        product_type,
                        tenure,
                        tenor_unit,
                        total_deposits,
                        total_withdrawals,
                        investment_status,
                        effective_date,
                        maturity_date,
                      } = request;
                      const selectRecord = () =>
                        setSelectedRecord({
                          id: investment_id,
                          investment_product_id: Number(investment_product_id),
                        });

                      return (
                        <tr key={investment_id}>
                          <td>{reference_number}</td>
                          <td>{investment_name || product_name}</td>
                          <td>
                            {product_type?.description ||
                              product_type?.name ||
                              "—"}
                          </td>
                          <td>{formatTenor(tenure, tenor_unit)}</td>
                          <td>{formatMoney(total_deposits, currencySymbol)}</td>
                          <td>
                            {formatMoney(
                              total_withdrawals ?? 0,
                              currencySymbol,
                            )}
                          </td>
                          <td>
                            <span
                              className={attachClassNameToTag(
                                investment_status,
                              )}
                            >
                              {attachClassNameToTag(investment_status)}
                            </span>
                          </td>
                          <td>{effective_date?.substring(0, 10)}</td>
                          <td>{maturity_date?.substring(0, 10) || "—"}</td>
                          <td>
                            <div className="dropdown">
                              <button
                                className="btn zoom-element btn-slim dropdown-toggle"
                                type="button"
                                id="dropdownMenuButton"
                                data-toggle="dropdown"
                                aria-haspopup="true"
                                aria-expanded="false"
                              ></button>
                              <div
                                className="dropdown-menu"
                                aria-labelledby="dropdownMenuButton"
                              >
                                {investment_status ===
                                InvestmentStatus.MATURED ? (
                                  <span
                                    className="cursor-pointer dropdown-item"
                                    id="custom-rollover-button"
                                    onClick={() => {
                                      handleRollOverModalToggle();
                                      selectRecord();
                                    }}
                                  >
                                    ROLL OVER
                                  </span>
                                ) : null}

                                <span
                                  className="cursor-pointer dropdown-item"
                                  onClick={() => {
                                    history.push(
                                      `/investment-details/${investment_id}`,
                                    );
                                  }}
                                >
                                  VIEW DETAILS
                                </span>
                                {!isFixedDeposit &&
                                  roles &&
                                  roles?.length > 0 &&
                                  roles?.includes(
                                    ROLE_TYPES.INVESTMENT_TOP_UP_INITIATOR,
                                  ) &&
                                  investment_status ===
                                    InvestmentStatus.ACTIVE && (
                                    <span
                                      className="cursor-pointer dropdown-item"
                                      id="custom-fund-investment-button"
                                      onClick={() => {
                                        handleFundInvestmentModalToggle();
                                        selectRecord();
                                      }}
                                    >
                                      FUND
                                    </span>
                                  )}

                                {roles &&
                                  roles?.length > 0 &&
                                  roles?.includes(
                                    ROLE_TYPES.INVESTMENT_WITHDRAWAL_INITIATOR,
                                  ) &&
                                  investment_status ===
                                    InvestmentStatus.ACTIVE && (
                                    <span
                                      className="cursor-pointer dropdown-item"
                                      id="custom-withdraw-investment-button"
                                      onClick={() => {
                                        handleInvestmentWithdrawalModalToggle();
                                        selectRecord();
                                      }}
                                    >
                                      {!isFixedDeposit
                                        ? "WITHDRAW"
                                        : "PREMATURE LIQUIDATION"}
                                    </span>
                                  )}
                                {investment_status ===
                                InvestmentStatus.MATURED ? (
                                  <span
                                    className="cursor-pointer dropdown-item"
                                    onClick={() => {
                                      setIsConfirmationModalOpen(true);
                                      setInvestmentID(investment_id);
                                      setMaturityDate(maturity_date || "");
                                    }}
                                  >
                                    CLOSE
                                  </span>
                                ) : null}
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {investmentsData?.totalCount > PAGE_SIZE ? (
                <div className="d-flex justify-content-end w-100 mt-3">
                  <Pagination
                    current={page}
                    pageSize={PAGE_SIZE}
                    total={investmentsData.totalCount}
                    showSizeChanger={false}
                    onChange={setPage}
                  />
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : !investmentsData ? (
        <div>
          <div className="grid__padding animated fadeInRight">
            <h4 className="center-txt py-5">
              <i className="fas fa-bell" /> There are currently no Investment
            </h4>
          </div>
        </div>
      ) : (
        <div>
          <div className="grid__padding animated fadeInRight">
            <h4 className="center-txt py-5">
              <i className="fas fa-bell" /> There are currently no Investment
            </h4>
          </div>
        </div>
      )}

      {isConfirmationModalOpen ? (
        <CloseInvestmentModal
          onConfirm={handleInvestmentClosure}
          onClose={() => {
            setIsConfirmationModalOpen(false);
            setInvestmentID(null);
            setMaturityDate("");
          }}
          isOpen={isConfirmationModalOpen}
          isLoading={isLoadingInvestmentClosure}
          maturityDate={maturityDate}
        />
      ) : null}
    </div>
  );
};

export default InvestorInvestment;
